// Search text, intros, related products and FAQs for the solutions pages (/solutions/*). Every statement here
// repeats what the product pages already say (data/numbers.js, sipTrunk.js, cloudPbx.js, teamsPhone.js,
// aiReceptionist.js), so nothing new is claimed.

const P = {
  pbx: { label: 'Cloud PBX', to: '/products/cloud-pbx' },
  sip: { label: 'SIP Trunking', to: '/products/sip-trunking' },
  teams: { label: 'Microsoft Teams Phone System', to: '/products/teams-phone-system' },
  ai: { label: 'AI Receptionist', to: '/products/ai-receptionist' },
  otp: { label: 'Voice OTP & Flash Calls', to: '/products/voice-otp' },
  did: { label: 'DID International Numbers', to: '/phone-numbers/did-international' },
  tollfree: { label: 'Toll-Free Numbers', to: '/phone-numbers/toll-free' },
  uifn: { label: 'UIFN Numbers', to: '/phone-numbers/uifn' },
  mobile: { label: 'Mobile Numbers', to: '/phone-numbers/mobile' },
  coverage: { label: 'Country coverage', to: '/coverage' },
};

const PORTING = { q: 'Can we keep our existing numbers?', a: 'Yes. We port geographic, non-geographic and 0800 numbers to VOIP2 with no loss of service, and keep your routing in place while the port completes.' };
const PBX_COMPAT = { q: 'Will VOIP2 work with our current phone system?', a: 'Almost certainly. Our SIP trunks work with the major on-premise and cloud PBX platforms, including 3CX, Asterisk, FreePBX, Cisco, Avaya and Yeastar, and with Microsoft Teams via Direct Routing.' };
const DOCS = { q: 'Do numbers need documents?', a: 'It depends on the country. Some numbers activate with no paperwork; others need proof of address or a local business registration. Each country page lists exactly what is required.' };

export const SOLUTIONS = {
  enterprise: {
    path: '/solutions/enterprise',
    seo: {
      title: 'Enterprise Phone Systems & Calling',
      description: 'Phone systems for multi-site organisations: Cloud PBX, SIP trunks, Microsoft Teams calling and local numbers in 140+ countries from VOIP2.',
    },
    featuresTitle: 'Built for large organisations',
    intro: {
      title: 'One voice provider for every site',
      paragraphs: [
        'VOIP2 gives multi-site organisations one provider for numbers and calling. Use the Cloud PBX where teams need a complete phone system, SIP trunks where a site keeps its own PBX, and Direct Routing to make and take calls in Microsoft Teams.',
        'Offices abroad can have local or national numbers in 140+ countries, routed to the same phone system with time-of-day rules and failover.',
      ],
    },
    products: [P.pbx, P.sip, P.teams, P.did, P.ai],
    faqs: [
      PORTING,
      PBX_COMPAT,
      { q: 'Can offices in other countries have local numbers?', a: 'Yes. Local and national numbers are available in 140+ countries. Callers dial a normal local number and the call reaches your phone system, SIP trunk or mobile wherever your team works.' },
    ],
  },
  contactCenters: {
    path: '/solutions/contact-centers',
    seo: {
      title: 'Contact Centre Telephony & Numbers',
      description: 'Toll-free, UIFN and local numbers with time-of-day routing and failover, delivered to your contact centre over Cloud PBX or SIP trunks.',
    },
    featuresTitle: 'Contact centre features',
    intro: {
      title: 'The numbers customers call, routed to your agents',
      paragraphs: [
        'Contact centres use VOIP2 for the numbers their customers call: toll-free numbers that are free to call within a country, UIFNs that are one +800 number for many countries, and local numbers in 140+ countries.',
        'Calls are routed by time of day, overflow to another site and fail over automatically, and are delivered to Cloud PBX queues or to your own platform over a SIP trunk. The AI Receptionist can answer first and hand callers to the right agent.',
      ],
    },
    products: [P.tollfree, P.uifn, P.pbx, P.sip, P.ai],
    faqs: [
      { q: 'Can customers call us for free?', a: 'Yes. With a toll-free number callers pay nothing, and you pay a per-minute inbound rate for the calls you receive. A UIFN gives you one +800 number that is free to call from many countries.' },
      { q: 'Can calls go to agents in different countries?', a: 'Yes. Numbers can be routed to a PBX, SIP trunk, mobile or the AI Receptionist wherever your agents work, with failover to the next destination if the first is unavailable.' },
      { q: 'Can calls be routed by time of day?', a: 'Yes. Send calls to different teams by hours, weekends and holidays, and set overflow to another site.' },
    ],
  },
  ucaas: {
    path: '/solutions/ucaas',
    seo: {
      title: 'UCaaS for Service Providers & Resellers',
      description: 'Wholesale DIDs, SIP trunks and a partner programme for service providers and resellers building unified communications services on VOIP2.',
    },
    featuresTitle: 'What providers get',
    intro: {
      title: 'Numbers and trunks for your own services',
      paragraphs: [
        'Carriers, resellers and UCaaS providers use VOIP2 to extend their coverage with wholesale DIDs in 140+ countries, including toll-free and UIFN numbers, and SIP trunks that work with the major PBX platforms.',
        'Every number shows its country requirements up front, and you order, verify and route numbers from one portal.',
      ],
    },
    products: [P.did, P.tollfree, P.uifn, P.sip, P.coverage],
    faqs: [
      PBX_COMPAT,
      DOCS,
      { q: 'Can we port numbers our customers already have?', a: 'Yes. We port numbers from the current provider with no loss of service, and keep routing in place while the port completes.' },
    ],
  },
  iot: {
    path: '/solutions/iot',
    seo: {
      title: 'IoT & M2M Voice and SMS Numbers',
      description: 'Virtual mobile numbers and voice connectivity for IoT and machine-to-machine services, with calls over SIP and messages sent to your systems.',
    },
    featuresTitle: 'Connectivity for devices',
    intro: {
      title: 'Numbers for devices, without SIM cards',
      paragraphs: [
        'VOIP2 mobile numbers come from genuine mobile ranges. Calls arrive over SIP and messages through our API, so devices, apps and platforms can use them without SIM cards.',
        'Inbound messages and call events are delivered to your systems by webhook, and Voice OTP flash calls can verify users and devices instead of an SMS one-time password.',
      ],
    },
    products: [P.mobile, P.otp, P.sip, P.coverage],
    faqs: [
      { q: 'Can a VOIP2 number receive SMS?', a: 'Yes, where the country supports it. Inbound messages are delivered to your system by webhook, and SMS support is shown per country.' },
      { q: 'Do we need SIM cards?', a: 'No. Calls arrive over SIP and messages through our API, so you can use numbers in apps, platforms and verification flows without SIM cards.' },
      DOCS,
    ],
  },
};
